/**
 * Modal Management Module
 * Centralized modal creation, display, and lifecycle management
 */

import { emit, on, Events } from "../../../engine/core/events.js";
import { escapeHtml } from "../../../engine/core/validation.js";
import { t } from "../../../shared/i18n/index.js";

// ============================================
// State
// ============================================

const activeModals = new Map(); // id -> { element, config, resolve, reject }
let modalStack = []; // Stack of modal IDs for proper closing order


// ============================================
// Modal Creation
// ============================================

/**
 * Create a modal element
 * @param {Object} config - Modal configuration
 * @param {string} config.id - Unique modal ID
 * @param {string} config.title - Modal title
 * @param {string} config.content - HTML content
 * @param {string} [config.className] - Additional CSS class
 * @param {boolean} [config.closable=true] - Show close button
 * @param {boolean} [config.closeOnBackdrop=true] - Close when clicking backdrop
 * @param {boolean} [config.closeOnEscape=true] - Close on Escape key
 * @param {Array} [config.buttons] - Button configurations
 * @returns {HTMLElement} Modal element
 */
export function createModal(config) {
  const {
    id,
    title,
    content,
    className = "",
    closable = true,
    closeOnBackdrop = true,
    closeOnEscape = true,
    buttons = [],
  } = config;

  const modal = document.createElement("div");
  modal.id = id;
  modal.className = `orbital-modal ${className}`;
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-labelledby", `${id}-title`);

  const buttonsHtml = buttons
    .map(
      (btn) => `
        <button
            class="modal-btn ${btn.className || ""}"
            data-action="${btn.action || ""}"
            ${btn.disabled ? "disabled" : ""}
        >
            ${escapeHtml(btn.text)}
        </button>
    `,
    )
    .join("");

  modal.innerHTML = `
        <div class="modal-backdrop"></div>
        <div class="modal-content">
            <div class="modal-header">
                <h2 id="${id}-title" class="modal-title">${escapeHtml(title)}</h2>
                ${closable ? `<button class="modal-close" aria-label="${escapeHtml(t("common.close"))}">&times;</button>` : ""}
            </div>
            <div class="modal-body">
                ${content}
            </div>
            ${buttons.length > 0 ? `<div class="modal-footer">${buttonsHtml}</div>` : ""}
        </div>
    `;

  // Store config
  activeModals.set(id, {
    element: modal,
    config: { closeOnBackdrop, closeOnEscape },
    resolve: null,
    reject: null,
  });

  // Event handlers
  if (closable) {
    modal.querySelector(".modal-close")?.addEventListener("click", () => {
      closeModal(id);
    });
  }

  if (closeOnBackdrop) {
    modal.querySelector(".modal-backdrop")?.addEventListener("click", () => {
      closeModal(id);
    });
  }

  // Button handlers
  modal.querySelectorAll(".modal-btn[data-action]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const action = btn.dataset.action;
      if (action === "close" || action === "cancel") {
        closeModal(id);
      }
      emit(Events.MODAL_CLOSE, { modalId: id, action });
    });
  });

  return modal;
}

// ============================================
// Modal Display
// ============================================

/**
 * Show a modal
 * @param {string|HTMLElement} modalOrId - Modal ID or element
 * @param {Object} [options] - Display options
 * @returns {Promise} Resolves when modal is closed
 */
export function showModal(modalOrId, options = {}) {
  return new Promise((resolve, reject) => {
    let modal;
    let id;

    if (typeof modalOrId === "string") {
      // Look up by ID
      id = modalOrId;
      const existing = activeModals.get(id);
      if (existing) {
        modal = existing.element;
      } else {
        // Try to find in DOM
        modal = document.getElementById(id);
        if (modal) {
          activeModals.set(id, {
            element: modal,
            config: options,
            resolve: null,
            reject: null,
          });
        }
      }
    } else {
      modal = modalOrId;
      id = modal.id;
    }

    if (!modal) {
      reject(new Error(`Modal not found: ${modalOrId}`));
      return;
    }

    // Store resolve/reject for later
    const entry = activeModals.get(id);
    if (entry) {
      entry.resolve = resolve;
      entry.reject = reject;
    }

    // Add to DOM if not already there
    if (!document.body.contains(modal)) {
      document.body.appendChild(modal);
    }

    // Show modal
    requestAnimationFrame(() => {
      modal.classList.add("active");
      modalStack.push(id);

      // Focus management: prioritize editable fields, then primary button, then first button
      const firstInput = modal.querySelector(
        'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])',
      );
      const primaryBtn = modal.querySelector(
        ".modal-btn-primary:not([disabled])",
      );
      const firstBtn = modal.querySelector(
        ".modal-btn:not([disabled]), button:not([disabled]):not(.modal-close)",
      );

      const focusTarget = firstInput || primaryBtn || firstBtn;
      if (focusTarget) {
        focusTarget.focus();
      }

      // Set up focus trap
      setupFocusTrap(modal);

      emit(Events.MODAL_OPEN, { modalId: id });
    });
  });
}

/**
 * Close a modal
 * @param {string} [id] - Modal ID (closes top modal if not specified)
 * @param {*} [result] - Result to pass to promise
 */
export function closeModal(id = null, result = null) {
  // If no ID specified, close the top modal
  if (!id && modalStack.length > 0) {
    id = modalStack[modalStack.length - 1];
  }

  if (!id) return;

  const entry = activeModals.get(id);
  if (!entry) return;

  const { element, resolve } = entry;

  // Hide modal
  element.classList.remove("active");
  // Firefox leaves focus on a field of the hidden modal (Chromium drops it):
  // the keys typed next went into that field, not to the app's shortcuts.
  if (element.contains(document.activeElement)) document.activeElement.blur();

  // Remove focus trap
  removeFocusTrap(element);

  // Remove from stack
  const stackIndex = modalStack.indexOf(id);
  if (stackIndex !== -1) {
    modalStack.splice(stackIndex, 1);
  }

  // Resolve promise
  if (resolve) {
    resolve(result);
  }

  emit(Events.MODAL_CLOSE, { modalId: id, result });
}

/**
 * Close all open modals
 */
export function closeAllModals() {
  [...modalStack].reverse().forEach((id) => {
    closeModal(id);
  });
}

// ============================================
// Modal State
// ============================================

/**
 * Check if a modal is open
 * @param {string} [id] - Modal ID (checks any modal if not specified)
 * @returns {boolean}
 */
export function isModalOpen(id = null) {
  if (id) {
    return modalStack.includes(id);
  }
  return modalStack.length > 0;
}

/**
 * Get the currently active (top) modal ID
 * @returns {string|null}
 */
export function getActiveModalId() {
  return modalStack.length > 0 ? modalStack[modalStack.length - 1] : null;
}

/**
 * Get modal element by ID
 * @param {string} id - Modal ID
 * @returns {HTMLElement|null}
 */
export function getModal(id) {
  const entry = activeModals.get(id);
  return entry ? entry.element : document.getElementById(id);
}

// ============================================
// Focus Trap
// ============================================

const focusTrapHandlers = new WeakMap();

/**
 * Set up focus trap within a modal
 * @param {HTMLElement} modal - Modal element
 */
function setupFocusTrap(modal) {
  // Remove existing handler if any
  const existingHandler = focusTrapHandlers.get(modal);
  if (existingHandler) {
    modal.removeEventListener("keydown", existingHandler);
  }

  const handler = (e) => {
    // Stop all keyboard events from reaching the background
    e.stopPropagation();

    // Handle Escape key to close modal
    if (e.key === "Escape") {
      e.preventDefault();
      closeModal(modal.id);
      return;
    }

    if (e.key !== "Tab") return;

    // Query focusable elements within the modal content (not backdrop)
    const modalContent = modal.querySelector(".modal-content") || modal;
    const focusableElements = Array.from(
      modalContent.querySelectorAll(
        'input:not([type="hidden"]):not([disabled]), button:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])',
      ),
    ).filter((el) => el.offsetParent !== null); // Only visible elements

    if (focusableElements.length === 0) return;

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const currentIndex = focusableElements.indexOf(document.activeElement);

    // Prevent default tab behavior
    e.preventDefault();

    if (e.shiftKey) {
      // Shift+Tab: go to previous, or wrap to last
      if (currentIndex <= 0) {
        lastFocusable.focus();
      } else {
        focusableElements[currentIndex - 1].focus();
      }
    } else {
      // Tab: go to next, or wrap to first
      if (currentIndex === -1 || currentIndex >= focusableElements.length - 1) {
        firstFocusable.focus();
      } else {
        focusableElements[currentIndex + 1].focus();
      }
    }
  };

  focusTrapHandlers.set(modal, handler);
  modal.addEventListener("keydown", handler);
}

/**
 * Remove focus trap from a modal
 * @param {HTMLElement} modal - Modal element
 */
function removeFocusTrap(modal) {
  const handler = focusTrapHandlers.get(modal);
  if (handler) {
    modal.removeEventListener("keydown", handler);
    focusTrapHandlers.delete(modal);
  }
}

// ============================================
// Escape Key Handler
// ============================================

function handleEscape(e) {
  if (e.key !== "Escape") return;

  const topModalId = getActiveModalId();
  if (!topModalId) return;

  const entry = activeModals.get(topModalId);
  if (entry?.config?.closeOnEscape !== false) {
    e.preventDefault();
    closeModal(topModalId);
  }
}

// ============================================
// Initialization
// ============================================

let initialized = false;

/**
 * Initialize modal management
 */
export function initModals() {
  if (initialized) return;

  document.addEventListener("keydown", handleEscape);
  initialized = true;

  console.log("[Modals] Module initialized");
}

/**
 * Cleanup modal management
 */
export function destroyModals() {
  document.removeEventListener("keydown", handleEscape);
  closeAllModals();
  activeModals.clear();
  modalStack = [];
  initialized = false;
}

// ============================================
// Convenience Functions
// ============================================

/**
 * Show a confirmation dialog
 * @param {Object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Message to display
 * @param {string} [options.confirmText='Confirm'] - Confirm button text
 * @param {string} [options.cancelText='Cancel'] - Cancel button text
 * @param {string} [options.confirmClass=''] - Confirm button class (e.g., 'danger')
 * @returns {Promise<boolean>} True if confirmed, false if cancelled
 */
export function confirm(options) {
  return new Promise((resolve) => {
    const id = `confirm-${Date.now()}`;

    const modal = createModal({
      id,
      title: options.title || t("common.confirm"),
      content: `<p>${escapeHtml(options.message)}</p>${options.details?.length
        ? `<ul>${options.details.map(detail => `<li>${escapeHtml(detail)}</li>`).join("")}</ul>`
        : ""}`,
      className: "modal-confirm",
      buttons: [
        {
          text: options.cancelText || t("common.cancel"),
          action: "cancel",
          className: "modal-btn-secondary",
        },
        {
          text: options.confirmText || t("common.confirm"),
          action: "confirm",
          className: `modal-btn-primary ${options.confirmClass || ""}`,
        },
      ],
    });

    // Override button handlers
    modal
      .querySelector('[data-action="cancel"]')
      .addEventListener("click", () => {
        closeModal(id);
        modal.remove();
        resolve(false);
      });

    modal
      .querySelector('[data-action="confirm"]')
      .addEventListener("click", () => {
        closeModal(id);
        modal.remove();
        resolve(true);
      });

    document.body.appendChild(modal);
    showModal(id);
  });
}

/**
 * Show the "access revoked" prompt used by both share-view and linked-doc
 * revocation paths. A consistent dialog regardless of where the user was
 * when the revocation arrived.
 *
 * "Later" resolves to false immediately — the notifications panel keeps
 * the entry, and the panel's own Dismiss path already has the data-loss
 * confirm guard. No need to double-prompt here.
 *
 * @param {Object} options
 * @param {string} options.docName - The name of the revoked document.
 * @param {string} [options.context] - Extra sentence describing the user's
 *   current context (e.g. "You were editing this document" vs
 *   "This document is linked in your current map").
 * @param {string} [options.recoverText='Recover now'] - Primary button.
 * @param {string} [options.laterText='Later'] - Secondary button.
 * @returns {Promise<boolean>} true = recover, false = later.
 */
export function showRevocationPrompt(options = {}) {
  const {
    docName,
    context,
    recoverText = t("app.revoked.recover"),
    laterText = t("common.later"),
  } = options;
  return new Promise((resolve) => {
    const id = `revocation-${Date.now()}`;

    const safeName = escapeHtml(docName || t("app.fallback.thisDocument"));
    // The name goes in bold inside the translated sentence.
    const lead = escapeHtml(t("app.revoked.lead")).replace("{name}", () => `<strong>${safeName}</strong>`);
    const contextHtml = context ? `<p class="revocation-context">${escapeHtml(context)}</p>` : "";

    const modal = createModal({
      id,
      title: t("app.revoked.title"),
      closable: false,
      closeOnBackdrop: false,
      closeOnEscape: false,
      className: "modal-revocation",
      content: `
        <div class="revocation-body">
          <p class="revocation-lead">
            ${lead}
          </p>
          ${contextHtml}
          <p class="revocation-explainer">
            ${escapeHtml(t("app.revoked.explainer"))}
          </p>
        </div>
      `,
      buttons: [
        {
          text: laterText,
          action: "later",
          className: "modal-btn-secondary",
        },
        {
          text: recoverText,
          action: "recover",
          className: "modal-btn-primary",
        },
      ],
    });

    modal.querySelector('[data-action="later"]').addEventListener("click", () => {
      closeModal(id);
      modal.remove();
      resolve(false);
    });

    modal.querySelector('[data-action="recover"]').addEventListener("click", () => {
      closeModal(id);
      modal.remove();
      resolve(true);
    });

    document.body.appendChild(modal);
    showModal(id);
  });
}

/**
 * Changes made on this device that the server will not store: this user may
 * now only view the map (decision 4 of docs/plans/2026-10-02-crdt-schema-v2.md;
 * engine/sync/refused-changes.js).
 * @param {{mapName: string, count: number|null}} options
 * @returns {Promise<'copy'|'discard'|'later'>} later: keep them on this
 *   device for now (asked again when the map is opened)
 */
export function showRefusedChangesPrompt({ mapName, count } = {}) {
  return new Promise((resolve) => {
    const id = `refused-changes-${Date.now()}`;
    const lead = typeof count === "number" && count > 0
      ? t("sync.refused.lead", { count })
      : t("sync.refused.leadUnknown");
    const modal = createModal({
      id,
      title: t("sync.refused.title"),
      closable: false,
      closeOnBackdrop: false,
      closeOnEscape: false,
      className: "modal-refused-changes",
      content: `
        <p><strong>${escapeHtml(mapName || t("app.fallback.thisDocument"))}</strong></p>
        <p>${escapeHtml(lead)}</p>
        <p>${escapeHtml(t("sync.refused.explainer"))}</p>
      `,
      buttons: [
        { text: t("common.later"), action: "later", className: "modal-btn-secondary" },
        { text: t("sync.refused.discard"), action: "discard", className: "modal-btn-secondary" },
        { text: t("sync.refused.copy"), action: "copy", className: "modal-btn-primary" },
      ],
    });
    for (const action of ["later", "discard", "copy"]) {
      modal.querySelector(`[data-action="${action}"]`).addEventListener("click", () => {
        closeModal(id);
        modal.remove();
        resolve(action);
      });
    }
    document.body.appendChild(modal);
    showModal(id);
  });
}

/**
 * Show an alert dialog
 * @param {Object} options - Dialog options
 * @param {string} options.title - Dialog title
 * @param {string} options.message - Message to display
 * @param {string} [options.buttonText='OK'] - Button text
 * @returns {Promise<void>} Resolves when dismissed
 */
export function alert(options) {
  return new Promise((resolve) => {
    const id = `alert-${Date.now()}`;

    const modal = createModal({
      id,
      title: options.title || t("app.modal.alert"),
      content: `<p>${escapeHtml(options.message)}</p>`,
      className: "modal-alert",
      buttons: [
        {
          text: options.buttonText || t("common.ok"),
          action: "ok",
          className: "modal-btn-primary",
        },
      ],
    });

    modal.querySelector('[data-action="ok"]').addEventListener("click", () => {
      closeModal(id);
      modal.remove();
      resolve();
    });

    document.body.appendChild(modal);
    showModal(id);
  });
}
